import React from "react";

export default function markdownToHtml(
  text: string,
  images: { filename: string; url: string; title?: string }[] = []
): React.ReactNode[] {
  if (typeof text !== "string") return [];

  const processBold = (input: string): React.ReactNode[] => {
    return input
      .split(/\*\*(.*?)\*\*/)
      .map((part, index) =>
        index % 2 === 1 ? <strong key={`bold-${index}`}>{part}</strong> : part
      );
  };

  const processItalic = (nodes: React.ReactNode[]): React.ReactNode[] => {
    return nodes.flatMap((node, index) => {
      if (typeof node !== "string") return node;
      return node
        .split(/\*(.*?)\*/)
        .map((part, idx) =>
          idx % 2 === 1 ? <em key={`italic-${index}-${idx}`}>{part}</em> : part
        );
    });
  };

  const processLinks = (nodes: React.ReactNode[]): React.ReactNode[] => {
    return nodes.flatMap((node, index) => {
      if (typeof node !== "string") return node;
      const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
      const result = [];
      let lastIndex = 0;
      let match;
      while ((match = regex.exec(node)) !== null) {
        const [fullMatch, linkText, url] = match;
        result.push(node.substring(lastIndex, match.index));
        result.push(
          <a
            key={`link-${index}-${match.index}`}
            href={url}
            target='_blank'
            rel='noopener noreferrer'
          >
            {linkText}
          </a>
        );
        lastIndex = match.index + fullMatch.length;
      }
      result.push(node.substring(lastIndex));
      return result;
    });
  };

  const processImages = (
    nodes: React.ReactNode[],
    images: { filename: string; url: string; title?: string }[]
  ): React.ReactNode[] => {
    return nodes.flatMap((node, index) => {
      if (typeof node !== "string") return node;
      const regex = /\(image:\s*([^)]+)\)/g;
      let lastIndex = 0;
      const elements = [];
      let match;
      while ((match = regex.exec(node)) !== null) {
        // Append text before the match
        const precedingText = node.substring(lastIndex, match.index);
        if (precedingText) {
          elements.push(precedingText);
        }
        const filename = match[1].trim();
        // Find the corresponding image from the images array
        const imageObj = images.find((img) => img.filename === filename);
        if (imageObj) {
          elements.push(
            <img
              key={`img-${index}-${match.index}`}
              src={imageObj.url}
              alt={imageObj.title || ""}
            />
          );
        } else {
          elements.push(match[0]);
        }
        lastIndex = regex.lastIndex;
      }
      const remainingText = node.substring(lastIndex);
      if (remainingText) {
        elements.push(remainingText);
      }
      return elements;
    });
  };

  const processInline = (
    input: string,
    images: { filename: string; url: string; title?: string }[]
  ): React.ReactNode[] => {
    let result = processBold(input);
    result = processItalic(result);
    result = processLinks(result);
    result = processImages(result, images);
    return result;
  };

  const lines = text.split("\n");
  const elements: React.ReactNode[] = [];
  let bulletGroup: string[] = [];

  const flushBulletGroup = () => {
    if (bulletGroup.length > 0) {
      elements.push(
        <ul key={`ul-${elements.length}`} className='list-disc list-inside'>
          {bulletGroup.map((line, idx) => (
            <li key={`li-${idx}`}>
              {processInline(line.replace(/^-+\s*/, "").trim(), images)}
            </li>
          ))}
        </ul>
      );
      bulletGroup = [];
    }
  };

  lines.forEach((line, idx) => {
    const trimmed = line.trim();

    if (trimmed.startsWith("### ")) {
      flushBulletGroup();
      elements.push(
        <h3 key={`h3-${idx}`}>{processInline(trimmed.substring(4), images)}</h3>
      );
    } else if (trimmed.startsWith("## ")) {
      flushBulletGroup();
      elements.push(
        <h2
          key={`h2-${idx}`}
          className='text-[#00638A] text-4xl md:text-5xl font-bold font-kalam'
        >
          {processInline(trimmed.substring(3), images)}
        </h2>
      );
    } else if (trimmed.startsWith("# ")) {
      flushBulletGroup();
      elements.push(
        <h1 key={`h1-${idx}`}>{processInline(trimmed.substring(2), images)}</h1>
      );
    } else if (trimmed.startsWith("-")) {
      bulletGroup.push(line);
    } else {
      flushBulletGroup();
      if (line !== "") {
        elements.push(
          <span key={`line-${idx}`}>{processInline(line, images)}</span>
        );
      }
      elements.push(<br key={`br-${idx}`} />);
    }
  });
  flushBulletGroup();

  return elements;
}
